import { useQueries } from '@tanstack/react-query';
import { use } from 'react';
import type { z } from 'zod';

import { ApiError } from '@/shared/http';
import {
  can,
  canForCatalog,
  usePermissions,
  type PermissionModel,
  type PermissionResource,
} from '@/shared/permissions';
import type { Permission } from '@/shared/screen';

import { blockPermissions, type Block, type BlockData, type SourceMap } from './block';
import { dataSourceQuery, isEmptyShape } from './data-source';
import { useBlockHost, type BlockSurface } from './host';
import { SourceFetchContext } from './internal/source-fetch';

/** Estado do bloco que o `BlockFrame` desenha (U39 §8–9). */
export type BlockState = 'loading' | 'ok' | 'empty' | 'error' | 'forbidden' | 'stale';

export interface BlockDataResult<S extends SourceMap, P = unknown> {
  readonly state: BlockState;
  /** Params do bloco já validados, com os defaults do zod. */
  readonly params: P;
  /** Presente em `ok`, `empty` e `stale`. */
  readonly data: BlockData<S> | undefined;
  readonly error: ApiError | undefined;
  /** Permissões que faltam a quem vê (vazio quando o 403 veio da API). */
  readonly missing: readonly Permission[];
  /** Instante da carga mais antiga entre as fontes (ms). */
  readonly updatedAt: number | undefined;
  readonly isFetching: boolean;
  /** Mostrando a página anterior enquanto a nova carrega (`keepPreviousData`). */
  readonly isPlaceholderData: boolean;
  readonly refetch: () => Promise<void>;
}

/**
 * Semântica da permissão por superfície: a tela oficial segue o `can()` do legado (U37-1); página e
 * prévia seguem o catálogo, fail-closed (D03/GU-17).
 */
export function isAllowed(
  surface: BlockSurface,
  model: readonly PermissionModel[] | undefined,
  permission: Permission,
): boolean {
  return surface === 'screen'
    ? can(
        model,
        permission.resource as PermissionResource,
        permission.action,
        permission.strict === undefined ? undefined : { strict: permission.strict },
      )
    : canForCatalog(model, permission.resource, permission.action);
}

/** Dados do bloco pelas fontes declaradas, com a chave de cache de cada fonte (09 §10.1). */
export function useBlockData<PS extends z.ZodType, S extends SourceMap>(
  block: Block<PS, S>,
  params: z.input<PS>,
): BlockDataResult<S, z.output<PS>> {
  const host = useBlockHost();
  const permissions = usePermissions();
  const fetchOverride = use(SourceFetchContext);

  const parsed = block.params.parse(params);
  const sourceParams = block.query(parsed) as Readonly<Record<string, unknown>>;
  const names = Object.keys(block.sources);
  const missing = permissions.isPending
    ? []
    : blockPermissions(block).sources.filter((p) => !isAllowed(host.surface, permissions.model, p));
  const enabled = permissions.isSuccess && missing.length === 0;

  const results = useQueries({
    queries: names.map((name) => {
      const source = block.sources[name];
      if (!source) throw new Error(`fonte ${name} ausente em ${block.id}`);
      const options = dataSourceQuery(source, sourceParams[name], fetchOverride ?? undefined);
      return { ...options, enabled };
    }),
  });

  const refetch = async () => {
    await Promise.all(results.map((result) => result.refetch()));
  };
  const isFetching = results.some((result) => result.isFetching);
  const isPlaceholderData = results.some((result) => result.isPlaceholderData);
  const base = { params: parsed, isFetching, isPlaceholderData, refetch };

  if (permissions.isError) {
    return {
      ...base,
      state: 'error',
      data: undefined,
      error: ApiError.from(permissions.error),
      missing: [],
      updatedAt: undefined,
    };
  }
  if (permissions.isPending)
    return {
      ...base,
      state: 'loading',
      data: undefined,
      error: undefined,
      missing: [],
      updatedAt: undefined,
    };
  if (missing.length > 0)
    return {
      ...base,
      state: 'forbidden',
      data: undefined,
      error: undefined,
      missing,
      updatedAt: undefined,
    };

  const error = results.find((result) => result.error)?.error ?? undefined;
  if (error?.status === 403)
    return {
      ...base,
      state: 'forbidden',
      data: undefined,
      error,
      missing: [],
      updatedAt: undefined,
    };

  const complete = results.every((result) => result.data !== undefined);
  if (!complete) {
    return error
      ? { ...base, state: 'error', data: undefined, error, missing: [], updatedAt: undefined }
      : {
          ...base,
          state: 'loading',
          data: undefined,
          error: undefined,
          missing: [],
          updatedAt: undefined,
        };
  }

  const data = Object.fromEntries(
    names.map((name, index) => [name, results[index]?.data]),
  ) as BlockData<S>;
  const updatedAt = Math.min(...results.map((result) => result.dataUpdatedAt));
  if (error) return { ...base, state: 'stale', data, error, missing: [], updatedAt };
  const empty = block.isEmpty
    ? block.isEmpty(data)
    : names.every((name, index) =>
        isEmptyShape(block.sources[name]?.shape ?? 'record', results[index]?.data),
      );
  return { ...base, state: empty ? 'empty' : 'ok', data, error: undefined, missing: [], updatedAt };
}
